/* eslint-disable no-eval */
import { useMemo, useState, useCallback, type ReactNode, Fragment } from 'react';
import { localizeWizard } from 'utils/helpers';
import { connect, Field } from 'formik';
import { get, isNil } from 'lodash';
import { useIntl, type IntlShape } from 'react-intl';
import MorphField from 'components/inputs';
import Checkbox from 'components/inputs/Checkbox';
import type { TFormValues } from 'components/inputs/types';
import FormattedHTMLMessage from 'components/common/FormattedHTMLMessage';
import { BUYING_HOUSE, NEW_APARTMENT, NEW_BUILDING, RENOVATION_HOUSE } from 'constants/constants';
import apartmentFeaturesSchema from 'constants/wizards/apartment-features-schema';
import buyingHouseFeaturesSchema from 'constants/wizards/buying-house-features-schema';
import buildHouseFeaturesSchema from 'constants/wizards/build-house-features-schema';
import renovationHouseFeaturesSchema from 'constants/wizards/renovation-house-features';
import type {
  IWizardFeature,
  IWizardFormField,
  TWizardFeatureGroup,
} from 'constants/wizards/types';
import Collapsible from 'react-collapsible';

const containsHTML = (str: string) => /<[a-z][\s\S]*>/i.test(str);

/** The feature schema of each project type (other types have none). */
export const projectTypeToSchemaMap: Partial<Record<string, IWizardFeature[]>> = {
  [NEW_APARTMENT]: apartmentFeaturesSchema,
  [NEW_BUILDING]: buildHouseFeaturesSchema,
  [BUYING_HOUSE]: buyingHouseFeaturesSchema,
  [RENOVATION_HOUSE]: renovationHouseFeaturesSchema,
};

// `values` is read by the evaluated `default_value_formula` (e.g. `values.house_area`); it is the
// first parameter so that it counts as used (it is only referenced inside the formula).
function getDefaultValue(values: TFormValues, field: IWizardFormField): unknown {
  let defaultValue: unknown = !isNil(field.defaultValue) ? field.defaultValue : null;
  if (isNil(defaultValue) && field.default_value_formula) {
    const evaluated: unknown = eval(field.default_value_formula);
    defaultValue = evaluated;
  }
  return defaultValue;
}

interface IFeatureFormFieldsProps {
  isOpen: boolean | undefined;
  feature: IWizardFeature;
  t: IntlShape['formatMessage'];
  values: TFormValues;
}

const FeatureFormFields = ({ isOpen, feature, t, values }: IFeatureFormFieldsProps) => (
  // The typings require `trigger`; the empty string renders the same empty trigger span as none.
  <Collapsible trigger="" open={isOpen} transitionTime={800} overflowWhenOpen="visible">
    {feature.form.map((field) => {
      const defaultValue = getDefaultValue(values, field);
      const { units, ...localizedProps } = field;
      return (
        <MorphField
          key={field.name}
          {...localizeWizard(localizedProps, {
            formatter: (id: unknown) => {
              if (!id) return id;
              const localizedLabel = t({ id: String(id), defaultMessage: ' ' });
              if (!containsHTML(localizedLabel)) return localizedLabel;
              return <FormattedHTMLMessage id={String(id)} defaultMessage=" " />;
            },
          })}
          {...(units ? { units } : {})}
          defaultValue={defaultValue}
        />
      );
    })}
  </Collapsible>
);

export interface IFeaturesProps {
  icon?: ReactNode;
  color?: string;
  group: TWizardFeatureGroup;
  projectType: string;
  /** Passed by the steps but not read: the features use the values of the formik context. */
  values?: TFormValues;
}

// The checkbox state of a feature is a boolean (or not set yet).
const toOpenState = (value: unknown) => (typeof value === 'boolean' ? value : undefined);

export default connect<IFeaturesProps, TFormValues>(
  ({ icon, color, group, projectType, formik: { values } }) => {
    const schema = useMemo(() => projectTypeToSchemaMap[projectType] || [], [projectType]);
    const { formatMessage: t } = useIntl();

    return (
      <>
        {schema
          .filter(({ group: _group }) => _group === group)
          .map((feature) => {
            const { name, label, description } = feature;
            const [isOpen, setIsOpen] = useState(toOpenState(get(values, `features.${name}`)));
            const handleToggle = useCallback((checked: boolean) => setIsOpen(checked), []);

            return (
              <Fragment key={name}>
                <Field
                  name={`features.${name}`}
                  component={Checkbox}
                  label={t({ id: label })}
                  icon={icon}
                  color={color}
                  description={
                    description ? t({ id: description, defaultMessage: '  ' }) : undefined
                  }
                  sideEffectOnChange={handleToggle}
                  skipScrollingOnChange
                />
                <FeatureFormFields {...{ isOpen, feature, t, values }} />
              </Fragment>
            );
          })}
      </>
    );
  },
);
